// TableFloatingMenuTooltip.js
// Plugin-view class that mounts the table floating menu.
// Follows the same tooltip pattern used by FloatingMenuTooltip.
//
// SOLID notes
// ──────────────────────────────────────────────────────────────────────────────
// S – Single Responsibility:
//     • State detection  → getTableMenuMode()  (pure, exported for tests)
//     • Anchor geometry  → getAnchorRect()      (pure, exported for tests)
//     • Tooltip geometry → positionTableTooltip() (DOM side-effect only)
//     • Command dispatch → tableFloatingMenuActions.js
//     • Plugin lifecycle → this class only
// O – Open/Closed: the command map lives in tableFloatingMenuActions.js so
//     new actions can be added without touching the tooltip.
// D – Dependency Inversion: this file depends on the abstract action interface
//     (isTableActionEnabled / dispatchTableAction), not on prosemirror-tables directly.

import {createRoot} from "react-dom/client";
import React from "react";
import {CellSelection} from "prosemirror-tables";
import {TableFloatingMenuComponent} from "./TableFloatingMenuComponent";
import {dispatchTableAction, isTableActionEnabled} from "./tableFloatingMenuActions";

// ─── Pure helpers ─────────────────────────────────────────────────────────────

/**
 * Detect whether the current editor state warrants showing the table menu.
 *
 * @param {EditorState} state
 * @returns {"cursor"|"range"|null}
 */
export function getTableMenuMode(state) {
    if (state.selection instanceof CellSelection) return "range";
    return null;
}

/**
 * Compute the bounding rect of the relevant cell(s) in document coordinates.
 *
 * For "range" mode: unions all .selectedCell element rects.
 * For "cursor" mode: walks up the DOM from the caret to the nearest td/th.
 *
 * Exported for unit-testing without a real plugin view.
 *
 * @param {EditorView} view
 * @param {"cursor"|"range"} mode
 * @returns {DOMRect|null}
 */
export function getAnchorRect(view, mode) {
    if (mode === "range") {
        const cells = Array.from(view.dom.querySelectorAll(".selectedCell"));
        if (cells.length === 0) return null;

        let top = Infinity, left = Infinity, bottom = -Infinity, right = -Infinity;
        for (const el of cells) {
            const r = el.getBoundingClientRect();
            if (r.top < top)    top    = r.top;
            if (r.left < left)  left   = r.left;
            if (r.bottom > bottom) bottom = r.bottom;
            if (r.right > right)   right  = r.right;
        }
        return {top, left, bottom, right, width: right - left, height: bottom - top};
    }

    // cursor mode — resolve the DOM node at the selection anchor
    const {from} = view.state.selection;
    const domAtPos = view.domAtPos(from);
    let node = domAtPos.node;
    if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;

    while (node && node !== view.dom) {
        const tag = node.tagName?.toLowerCase();
        if (tag === "td" || tag === "th") return node.getBoundingClientRect();
        node = node.parentElement;
    }
    return null;
}

/**
 * Position the tooltip element relative to its offsetParent.
 *
 * @param {HTMLElement} tooltip
 * @param {object} anchorRect  — {top, left, bottom, right} in viewport coords
 */
function positionTableTooltip(tooltip, anchorRect) {
    const box = tooltip.offsetParent?.getBoundingClientRect();
    if (!box) return;

    const {width: tooltipWidth, height: tooltipHeight} = tooltip.getBoundingClientRect();
    const padding = 8;
    const gap = 6;

    // Horizontal: center on the anchor, clamp to editor bounds.
    const anchorCenter = (anchorRect.left + anchorRect.right) / 2;
    let leftRel = anchorCenter - box.left - tooltipWidth / 2;
    leftRel = Math.max(padding, Math.min(leftRel, box.width - tooltipWidth - padding));
    tooltip.style.left = `${leftRel}px`;
    tooltip.style.transform = "none";

    // Vertical: prefer above the anchor, fall back to below.
    const topIfAbove = anchorRect.top - box.top - tooltipHeight - gap;
    const topIfBelow = anchorRect.bottom - box.top + gap;
    let topRel = topIfAbove >= padding ? topIfAbove : topIfBelow;
    topRel = Math.max(padding, Math.min(topRel, box.height - tooltipHeight - padding));
    tooltip.style.top  = `${topRel}px`;
    tooltip.style.bottom = "";
}

// ─── Plugin view ──────────────────────────────────────────────────────────────

/**
 * TableFloatingMenuTooltip — ProseMirror plugin view.
 *
 * Lifecycle: constructed once per editor view, update() called on every state
 * change, destroy() on teardown.  Mirrors FloatingMenuTooltip for consistency.
 */
export class TableFloatingMenuTooltip {
    constructor(view) {
        this.view = view;
        this._destroyed = false;
        this._mode = null;      // "cursor" | "range" | null — last rendered mode
        this._rafId = null;     // pending requestAnimationFrame id

        // Stable callback references — created once, never re-bound.
        // Satisfies the Open/Closed principle: the component never needs to
        // know how commands are dispatched.
        this._onAction  = (actionType) => dispatchTableAction(actionType, this.view);
        this._isEnabled = (actionType) => isTableActionEnabled(actionType, this.view.state, this.view);

        // Keyboard a11y: Escape collapses the selection and hides the menu.
        this._handleKeyDown = this._onKeyDown.bind(this);
        view.dom.addEventListener("keydown", this._handleKeyDown);

        this.tooltip = this._createTooltip();
        view.dom.parentNode.appendChild(this.tooltip);

        this.update(view, null);
    }

    // ── Private ──────────────────────────────────────────────────────────────

    _createTooltip() {
        const tooltip = document.createElement("div");
        tooltip.className = "pm-table-floating-menu";
        tooltip.style.cssText = `
            position: absolute;
            display: none;
            z-index: 1001;
            pointer-events: auto;
            white-space: nowrap;
            width: max-content;
            max-width: none;
        `;
        this.root = createRoot(tooltip);
        return tooltip;
    }

    /**
     * Render (or re-render) the React tree.
     * Only called when mode changes — callbacks are stable refs so React
     * will not re-create child components unnecessarily.
     */
    _renderReact(mode) {
        if (this._destroyed) return;
        this.root.render(
            <TableFloatingMenuComponent
                mode={mode}
                onAction={this._onAction}
                isEnabled={this._isEnabled}
            />
        );
    }

    _onKeyDown(event) {
        if (event.key !== "Escape") return;
        if (this.tooltip.style.display === "none") return;

        event.preventDefault();
        const {state, dispatch} = this.view;
        // Collapse to cursor — hides the menu on next update().
        dispatch(state.tr.setSelection(
            state.selection.constructor.near
                ? state.selection.constructor.near(state.selection.$to)
                : state.selection
        ));
        this.view.focus();
    }

    _hide() {
        this.tooltip.style.display = "none";
        this._mode = null;
    }

    _schedulePosition(view, mode) {
        // Cancel any previously scheduled rAF so rapidly firing update()
        // calls don't stack up. The _destroyed guard inside the callback
        // handles the case where destroy() races a pending rAF.
        if (this._rafId !== null) cancelAnimationFrame(this._rafId);
        this._rafId = requestAnimationFrame(() => {
            this._rafId = null;
            if (this._destroyed || this.tooltip.style.display === "none") return;
            const anchorRect = getAnchorRect(view, mode);
            if (anchorRect) positionTableTooltip(this.tooltip, anchorRect);
        });
    }

    // ── ProseMirror plugin-view interface ────────────────────────────────────

    update(view, lastState) {
        const {state} = view;

        // Skip if neither doc nor selection changed.
        if (
            lastState &&
            lastState.doc.eq(state.doc) &&
            lastState.selection.eq(state.selection)
        ) return;

        const mode = getTableMenuMode(state);

        if (!mode) {
            this._hide();
            return;
        }

        // Re-render only when the menu mode changes — avoids React reconciliation
        // on every cursor keystroke inside a cell.
        if (mode !== this._mode) {
            this._mode = mode;
            this._renderReact(mode);
        }

        this.tooltip.style.display = "";
        this._schedulePosition(view, mode);
    }

    destroy() {
        this._destroyed = true;
        this.view.dom.removeEventListener("keydown", this._handleKeyDown);

        // Cancel pending rAF before unmounting to prevent post-destroy DOM writes.
        if (this._rafId !== null) {
            cancelAnimationFrame(this._rafId);
            this._rafId = null;
        }

        const root    = this.root;
        const tooltip = this.tooltip;
        // Defer unmount to avoid "Cannot flush updates when React is already rendering"
        // error when ProseMirror tears down the view inside a React render cycle.
        queueMicrotask(() => {
            root.unmount();
            tooltip.remove();
        });
    }
}
